const QUICKCAL_BOOKING_BASE_URL = 'https://book.quickcasa.ai'

interface BookingPrefillRenter {
  quickPassUserId: string
  name: string
  email: string
}

/**
 * Builds a QuickCal public booking URL for a given agent + event-type slug,
 * pre-filled with the signed-in renter's identity so they never have to retype
 * their name/email and the resulting booking is attributed to their QuickPass
 * account. The live booking app (book.quickcasa.ai) reads `prefillName` /
 * `prefillEmail` to pre-fill the guest form and `qp` to attribute the booking to
 * the renter. This is a navigation URL, not a fetch, so it is cross-origin-safe
 * between the QuickPass app and book.quickcasa.ai.
 *
 * @param {string} agentUserId - The QC user id that owns the booking page.
 * @param {string} eventTypeSlug - The event type's URL slug.
 * @param {BookingPrefillRenter} renter - The signed-in renter's identity to pre-fill.
 * @returns {string} A fully-qualified, pre-filled QuickCal booking URL.
 */
const buildPrefilledBookingUrl = (
  agentUserId: string,
  eventTypeSlug: string,
  renter: BookingPrefillRenter,
): string => {
  const queryParameters = new URLSearchParams()

  if (renter.name.length > 0) {
    queryParameters.set('prefillName', renter.name)
  }
  if (renter.email.length > 0) {
    queryParameters.set('prefillEmail', renter.email)
  }
  if (renter.quickPassUserId.length > 0) {
    queryParameters.set('qp', renter.quickPassUserId)
  }

  const queryString = queryParameters.toString()
  const baseUrl = `${QUICKCAL_BOOKING_BASE_URL}/${agentUserId}/${eventTypeSlug}`

  return queryString.length > 0 ? `${baseUrl}?${queryString}` : baseUrl
}

export { buildPrefilledBookingUrl }
